import CONF from 'config/conf.singleton';
import H from 'constants/httpMethod.const';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import fromAnyToNumber from 'utils/converters/fromAnyToNumber.util';
import { nowTillLastYear } from 'utils/date/now.util';
import alwaysSame from 'utils/functions/nullary/alwaysSame.util';
import actionEndpointBe from 'utils/mw/actionEndpointBe.util';
import parseDate from 'utils/parsers/parseDate';
import isFullArray from 'utils/predicates/array/isFullArray.util';
import { isNotValidId } from 'utils/predicates/fields.util';

// Normalizes scheduled/paginated bookings response coming from BE
const fromDtoFutureBookings = (dto) => {
  const results = asArray(dto?.results).map((item) => {
    const procedures = asArray(item?.procedures).map((p) => ({
      ...p,
      // Ensure numeric prices and default duration/processing when missing
      price: p?.price == null ? 0 : fromAnyToNumber(p.price),
      duration: p?.duration ?? '00:00:00',
      processing: p?.processing ?? '00:00:00',
    }));

    // ensure procedures are ordered by start_time
    if (isFullArray(procedures)) {
      procedures.sort((a, b) => (parseDate(a?.start_time) || 0) - (parseDate(b?.start_time) || 0));
    }

    return {
      ...item,
      procedures,
      notes: item?.notes ?? '',
      sms_records: asArray(item?.sms_records),
      deposit_paid: item?.deposit_paid ?? null,
    };
  });

  return {
    count: fromAnyToNumber(dto?.count) || results.length,
    next: dto?.next ?? null,
    previous: dto?.previous ?? null,
    results,
  };
};

// Normalizes past bookings response (history) from BE
const fromDtoPastBookings = (dto) => {
  const results = asArray(dto?.results)
    .map((item) => {
      const appointment = item?.appointment;
      if (!appointment) return null;

      const procedures = asArray(appointment?.procedures).map((p) => ({
        ...p,
        price: p?.price == null ? 0 : fromAnyToNumber(p.price),
        duration: p?.duration ?? '00:00:00',
        processing: p?.processing ?? '00:00:00',
      }));

      // ensure procedures are ordered by start_time
      if (procedures.length) {
        procedures.sort((a, b) => (parseDate(a?.start_time) || 0) - (parseDate(b?.start_time) || 0));
      }

      return {
        ...appointment,
        timestamp: item?.timestamp,
        procedures,
        notes: appointment?.notes ?? '',
        sms_records: asArray(appointment?.sms_records),
        deposit_paid: null,
        transaction: item?.transaction ?? null,
      };
    })
    .filter(alwaysSame);

  return {
    results,
    total_bookings_count: fromAnyToNumber(dto?.total_bookings_count) || results.length,
    _v: dto?._v ?? null,
  };
};

const fromDataToDtoPastBookings = (data) => ({
  page_size: data?.pageSize ?? data?.page_size,
});

export const beClientProfileOverview = actionEndpointBe('beClientProfileOverview', H.get, 'salon/:salonId/client/:clientId/', (options, extra) => {
  const clientId = fromAnyToTrimmed(extra?.clientId) || 0;

  return {
    ...options,
    isMissingParams: isNotValidId(clientId),
    params: { clientId },
    inert: true,
  };
});

export const beClientProfileBookingsStats = actionEndpointBe('beClientProfileBookingsStats', H.get, 'metrics/appointment/client/noshow/:salonId/:clientId/:fromDate/:toDate/', (options, extra) => {
  const clientId = fromAnyToTrimmed(extra?.clientId) || 0;
  const { fromDate, toDate } = nowTillLastYear();

  return {
    ...options,
    isMissingParams: isNotValidId(clientId),
    params: {
      clientId,
      fromDate,
      toDate,
    },
    inert: true,
    queryKey: [options.name, H.get, clientId],
    // we only pass the clientId and not from/to dates as we need to update this query only when we open new client
  };
});

export const beClientProfileCreate = actionEndpointBe('beClientProfileCreate', H.post, 'salon/:salonId/client/', (options, extra) => ({
  ...options,
  inert: true,
  isMissingParams: false,
  data: extra?.data,
}));

export const beClientProfileUpdate = actionEndpointBe('beClientProfileUpdate', H.put, 'salon/:salonId/client/:clientId/', (options, extra) => {
  const clientId = fromAnyToTrimmed(extra?.clientId) || 0;

  return {
    ...options,
    inert: true,
    isMissingParams: isNotValidId(clientId),
    params: { clientId },
    data: extra?.data,
  };
});

export const beClientProfileDelete = actionEndpointBe('beClientProfileDelete', H.del, 'salon/:salonId/client/:clientId/', (options, extra) => {
  const clientId = fromAnyToTrimmed(extra?.clientId) || 0;

  return {
    ...options,
    inert: true,
    isMissingParams: isNotValidId(clientId),
    params: { clientId },
  };
});

export const beClientProfileBlockFromNcob = actionEndpointBe('beClientProfileBlockFromNcob', H.patch, 'salon/v3/:salonId/block/ncob/client/:clientId/', (options, extra) => {
  const clientId = fromAnyToTrimmed(extra?.clientId) || 0;

  return {
    ...options,
    inert: true,
    isMissingParams: isNotValidId(clientId),
    params: { clientId },
    data: extra?.data,
  };
});

export const beClientProfileFutureBookings = actionEndpointBe('beClientProfileFutureBookings', H.get, 'salon/v2/:salonId/client/:clientId/scheduled/?page=:page', (options, extra) => {
  const clientId = fromAnyToTrimmed(extra?.clientId) || 0;
  const page = fromAnyToNumber(fromAnyToTrimmed(extra?.page)) || 1;

  return {
    ...options,
    isMissingParams: isNotValidId(clientId) || !extra?.enabled,
    params: { clientId, page },
    inert: true,
    fromDto: fromDtoFutureBookings,
  };
});

export const beClientProfilePastBookings = actionEndpointBe(
  'beClientProfilePastBookings',
  H.get,
  'salon/v2/:salonId/client/:clientId/history/?before=:before&page_size=:pageSize',
  (options, extra) => {
    const clientId = fromAnyToTrimmed(extra?.clientId) || 0;
    const before = fromAnyToTrimmed(extra?.before) || '';

    const pageSize = fromAnyToNumber(fromAnyToTrimmed(extra?.pageSize)) || 10;

    return {
      ...options,
      isMissingParams: isNotValidId(clientId) || !extra?.enabled,
      params: { clientId, before, pageSize },
      inert: true,
      fromDto: fromDtoPastBookings,
      toDto: fromDataToDtoPastBookings,
    };
  },
);

// Normalizes history response for the sales tab (products & vouchers).
// Unlike fromDtoPastBookings, this preserves items without an appointment
// (standalone retail / voucher sales where appointment is null).
const fromDtoSalesHistory = (dto) => {
  const results = asArray(dto?.results)
    .map((item) => {
      const appointment = item?.appointment;
      const transaction = item?.transaction;
      const voucher = item?.voucher;

      if (!transaction && !voucher) return null;

      return {
        ...(appointment || {}),
        timestamp: item?.timestamp,
        transaction: transaction || null,
        voucher: voucher || null,
      };
    })
    .filter(alwaysSame);

  return {
    results,
    total_bookings_count: fromAnyToNumber(dto?.total_bookings_count) || results.length,
    _v: dto?._v ?? null,
  };
};

export const beClientProfileSalesHistory = actionEndpointBe(
  'beClientProfileSalesHistory',
  H.get,
  'salon/v2/:salonId/client/:clientId/history/?before=:before&page_size=:pageSize',
  (options, extra) => {
    const clientId = fromAnyToTrimmed(extra?.clientId) || 0;
    const before = fromAnyToTrimmed(extra?.before) || '';
    const pageSize = fromAnyToNumber(fromAnyToTrimmed(extra?.pageSize)) || 10;

    return {
      ...options,
      isMissingParams: isNotValidId(clientId),
      params: { clientId, before, pageSize },
      inert: true,
      fromDto: fromDtoSalesHistory,
      toDto: fromDataToDtoPastBookings,
    };
  },
);

export const beClientProfileDeposits = actionEndpointBe('beClientProfileDeposits', H.get, 'deposit/:salonId/client/:clientId/', (options, extra) => {
  const clientId = fromAnyToTrimmed(extra?.clientId) || 0;

  return {
    ...options,
    base: CONF.transactionApiRoot,
    isMissingParams: isNotValidId(clientId),
    params: { clientId },
    inert: true,
  };
});

export const beClientProfileAddDeposit = actionEndpointBe('beClientProfileAddDeposit', H.post, 'deposit/:salonId/create/', (options, extra) => {
  const clientId = fromAnyToTrimmed(extra?.clientId) || 0;

  return {
    ...options,
    base: CONF.transactionApiRoot,
    isMissingParams: isNotValidId(clientId),
    params: { clientId },
    inert: true,
    data: extra?.data,
  };
});

export const beClientProfileSalonTags = actionEndpointBe('beClientProfileSalonTags', H.get, 'salon/v2/:salonId/client_tags/', (options) => ({
  ...options,
  inert: true,
  isMissingParams: false,
}));

const fromDtoClientSms = (dto) => ({
  messages: asArray(dto?.messages).map((msg) => ({
    id: msg?.id,
    number: msg?.number ?? '',
    smsType: msg?.sms_type ?? '',
    status: msg?.status ?? '',
    text: msg?.text ?? '',
    timestamp: msg?.timestamp ?? '',
    twilioStatus: msg?.twilio_status ?? '',
  })),
});

export const beClientProfileSmsRead = actionEndpointBe('beClientProfileSmsRead', H.get, 'salon/v2/:salonId/marketing/sms/client/:clientId/', (options, extra) => {
  const clientId = fromAnyToTrimmed(extra?.clientId) || 0;

  return {
    ...options,
    isMissingParams: isNotValidId(clientId) || !extra?.enabled,
    params: { clientId },
    inert: true,
    fromDto: fromDtoClientSms,
  };
});

const fromDtoClientEmail = (dto) => ({
  results: asArray(dto?.results).map((item) => ({
    id: item?.id,
    name: item?.name ?? '',
    campaignSent: item?.campaign_sent ?? '',
    campaignUrl: item?.campaign_url ?? '',
    status: item?.status ?? '',
  })),
});

export const beClientProfileEmailRead = actionEndpointBe('beClientProfileEmailRead', H.get, 'salon/v3/:salonId/marketing/email/client/:clientId/', (options, extra) => {
  const clientId = fromAnyToTrimmed(extra?.clientId) || 0;

  return {
    ...options,
    isMissingParams: isNotValidId(clientId) || !extra?.enabled,
    params: { clientId },
    inert: true,
    fromDto: fromDtoClientEmail,
  };
});
